import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import apiClient from '../api/client';

function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true); // Start with loading = true
  const [error, setError] = useState(null);
  const [schema, setSchema] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [runningBacktest, setRunningBacktest] = useState(false);

  // Form state
  const [symbol, setSymbol] = useState('BTCUSDT');
  const [timeframe, setTimeframe] = useState('15m');
  const [startDate, setStartDate] = useState('2024-01-01');
  const [endDate, setEndDate] = useState('2024-12-31');
  const [initialCapital, setInitialCapital] = useState(10000);
  const [params, setParams] = useState({});

  useEffect(() => {
    loadSchema();
    loadRecentTasks();
  }, []);

  const loadSchema = async () => {
    try {
      const response = await apiClient.get('/strategies/params-schema');
      setSchema(response.data);
      setParams(response.data.default_params || {});
      setLoading(false);
    } catch (err) {
      setError('Failed to load strategy schema: ' + err.message);
      setLoading(false);
    }
  };

  const loadRecentTasks = async () => {
    // TODO: Implement task list endpoint
    setTasks([]);
  };

  const handleRunBacktest = async () => {
    setRunningBacktest(true);
    setError(null);

    try {
      const requestData = {
        symbol,
        timeframe,
        start_date: `${startDate}T00:00:00`,
        end_date: `${endDate}T23:59:59`,
        initial_capital: initialCapital,
        commission_rate: 0.00075,
        params,
      };

      const response = await apiClient.post('/backtest/run', requestData);
      const taskId = response.data.task_id;

      // Navigate to results page
      navigate(`/backtest/${taskId}`);
    } catch (err) {
      setError('Failed to start backtest: ' + (err.response?.data?.detail || err.message));
      setRunningBacktest(false);
    }
  };

  if (!schema) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Run Backtest
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Symbol"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value)}
              >
                {schema.symbols?.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Timeframe"
                value={timeframe}
                onChange={(e) => setTimeframe(e.target.value)}
              >
                {schema.timeframes?.map((tf) => (
                  <MenuItem key={tf} value={tf}>
                    {tf}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Initial Capital"
                value={initialCapital}
                onChange={(e) => setInitialCapital(parseFloat(e.target.value))}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                Strategy Parameters
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="HHLL Range"
                value={params.hhll_range || 30}
                onChange={(e) => setParams({ ...params, hhll_range: parseInt(e.target.value) })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="ATR TP Multiplier"
                value={params.atr_tp_mult || 2.7}
                onChange={(e) => setParams({ ...params, atr_tp_mult: parseFloat(e.target.value) })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Max Slots"
                value={params.max_slots || 10}
                onChange={(e) => setParams({ ...params, max_slots: parseInt(e.target.value) })}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label="Cooldown Bars"
                value={params.cooldown_bars || 2}
                onChange={(e) => setParams({ ...params, cooldown_bars: parseInt(e.target.value) })}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={params.use_trend_filter !== false}
                    onChange={(e) => setParams({ ...params, use_trend_filter: e.target.checked })}
                  />
                }
                label="Use Trend Filter"
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                onClick={handleRunBacktest}
                disabled={runningBacktest || loading}
                sx={{ mt: 2 }}
              >
                {runningBacktest ? <CircularProgress size={24} /> : 'Run Backtest'}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      <Grid item xs={12} md={4}>
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Recent Tasks
          </Typography>
          {tasks.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No recent tasks
            </Typography>
          ) : (
            <List>
              {tasks.map((task) => (
                <ListItem key={task.id} button onClick={() => navigate(`/backtest/${task.id}`)}>
                  <ListItemText primary={task.symbol} secondary={task.status} />
                </ListItem>
              ))}
            </List>
          )}
        </Paper>
      </Grid>
    </Grid>
  );
}

export default Dashboard;

